import cn from 'classnames'
import { IHRDatasetInfo } from 'legacy/lut/classes/helpers/partner360/HistoricalResultsHelper'
import { usePartnerId } from 'legacy/lut/classes/helpers/partner360/usePartnerId'
import { usePartnerInfo } from 'legacy/lut/classes/helpers/partner360/usePartnerInfo'
import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { formatMoneyInteger } from 'legacy/lut/components/widgets/public/partner360/helpers/formatMoneyInteger'
import { selectGlobalPartnerData, selectGPLoadingStatus } from 'legacy/lut/features/globalPartner'
import { useJbpSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Switch } from 'core-components/library/checkbox'
import { Checkbox } from 'core-components/library/checkbox'
import { LoaderOverlay } from 'core-components/library/loader'
import { TPopupHorizontal } from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'
import MeasureController from 'core/analytics/MeasureController'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { ActualsTypeButtons, useActualsTypes } from 'partner360/components/actualsTypeButtons'
import { CurrenciesButtons, useCurrencies } from 'partner360/components/currenciesButtons'
import { SellTypeButtons } from 'partner360/components/sellTypeButtons/SellTypeButtons'
import { isSubCategoryMandatory } from 'partner360/entities/planning'
import { isSectionSettingOn } from 'partner360/helpers/sectionSettings/isSectionSettingOn'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import {
  selectPartnerAccess,
  selectSaleType,
} from '../../../../../../features/partner360/configuration'
import { selectSectionSettings } from '../../../../../../features/partner360/sectionSettings'
import { CommentIcon } from '../../components/CommentIcon'
import { GrowField } from '../../components/GrowField'
import { HelpSection } from '../../helpButton'
import { HintComponent } from '../../HintComponent'
import { useMarketShare } from '../../hooks/useMarketShare'
import { IJBPPlanning, IJBPPlanningItem } from '../../models/IJBPPlanning'
import { WidgetHeaderWithHelpButton } from '../../widgetHeader'
import { fieldWrapper } from '../components/fieldWrapper/fieldWrapper'
import { PartnersCompletenessField } from '../components/partnersCompletenessField/PartnersCompletenessField'
import { SplitPlanningSwitcher } from '../components/splitPlanningSwitcher/SplitPlanningSwitcher'
import {
  actualsColor,
  IS_ALWAYS_SHOW_ACTUALS,
  IS_QUARTER_DEFAULT,
  IS_SHOW_NULL,
  IS_SHOW_PREV_PREV_FYEAR,
  IS_SHOW_PREV_RECALC_ACTUALS,
  OVERALL_CATEGORY,
  PLANNING_BY_FY_FQ,
  PLANNING_COL_NUMBER,
  quotaColor,
  SOB_FRACTION_DIGITS,
  targetsColor,
} from '../config'
import { calcDiff } from '../helpers/calcDiff'
import { calcPercentage } from '../helpers/calcPercentage'
import { getCategoryNAField } from '../helpers/getCategoryNAField'
import { getCompletenessItem } from '../helpers/getCompletenessItem'
import { getIsCollapsedCategory } from '../helpers/getIsCollapsedCategory'
import { getNAField } from '../helpers/getNAField'
import { isContractualsByFlag } from '../helpers/partnerFlags/isContractualsByFlag'
import {
  IPreparePlanningItems,
  preparePlanningItems,
} from '../helpers/prepareData/preparePlanningItems'
import { CategoryHeaderStandard } from '../lookAndFeel/components/categoryHeader/CategoryHeaderStandard'
import { TSplitType } from '../models'
import { IGlobalCategory } from '../models/IGlobalCategory'
import { IPartnerFlag } from '../models/IPartnerFlag'
import { IPartnersCompleteness } from '../models/IPartnersCompleteness'

import styles from './CategoryPlanningStandard.module.scss'
import stylesCompensation from 'legacy/lut/components/widgets/public/partner360/Compensation.module.scss'
import stylesTable from 'legacy/lut/components/widgets/public/partner360/PartnerDevicesInfo.module.scss'

interface IProps {
  widgetParams: IObjectAny
  pid: string
  metrics: MeasureController

  planningData: IJBPPlanning[]

  formats: IFormActualsTargetsSettings[]
  allDataSets: IHRDatasetInfo[]
  dataSetInfo: IHRDatasetInfo

  showActuals: boolean
  showQuota: boolean
  setShowActuals: (showActuals: boolean) => void
  setShowQuota: (showQuota: boolean) => void
  isRegionalPlanning: boolean
  setIsRegionalPlanning: (isRegionalPlanning: boolean) => void

  isShowFutureYearsPlans: boolean

  isContractualPlanning: boolean
  setIsContractualPlanning?: (isContractualPlanning: boolean) => void
  isContractualsAvailable: boolean

  isShowOnlyCompletedPlans: boolean
  setIsShowOnlyCompletedPlans?: (isShowOnlyCompletedPlans: boolean) => void

  composedReportMode?: boolean
  composedReportFYear?: number
  fullTotalMode?: boolean

  partnerFlagsState: IPartnerFlag[]

  splitType: TSplitType
  setSplitType: Dispatch<SetStateAction<TSplitType>>

  globalCategory?: IGlobalCategory
  urlActualsValueType?: string
}

export const TotalPlanningStandard = (props: IProps) => {
  /** -------------------- States ----------------------- */

  const [actualsItems, setActualsItems] = useState<IJBPPlanningItem[]>([])
  const [hideIfEmpty, setHideIfEmpty] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [isDataPresent, setIsDataPresent] = useState(false)
  const [isShowAllSplits, setIsShowAllSplits] = useState(false)
  const [isShowPrevTargets, setIsShowPrevTargets] = useState(false)
  const [splitType, setSplitType] = useState<TSplitType>('Total')
  const [targetsItems, setTargetsItems] = useState<IJBPPlanningItem[]>([])
  const [targetsRollupItems, setTargetsRollupItems] = useState<IJBPPlanningItem[]>([])
  const [actualsRollupItems, setActualsRollupItems] = useState<IJBPPlanningItem[]>([])

  /** -------------------- Selectors ----------------------- */

  const sectionSettings = useSelector(selectSectionSettings)
  const saleType = useSelector(selectSaleType)
  const { isDistributor, isRegionalDistributor } = useSelector(selectPartnerAccess)
  const isEditMode = useSelector(selectPublishingMode)
  const globalPartnerData = useSelector(selectGlobalPartnerData)
  const gpDataLoadingStatus = useSelector(selectGPLoadingStatus)

  /** -------------------- Hooks ----------------------- */

  const partnerId = usePartnerId(props.widgetParams, props.pid)
  const {
    fyear: partnerFYear,
    partnerInfo,
    canEditPartner,
    buttonCurrType,
  } = usePartnerInfo({
    partnerId,
  })
  const { jbpSettings } = useJbpSettings()
  const { activeCurrency: currency } = useCurrencies()
  const { actualsType, actualsTypes, onChangeActualsType } = useActualsTypes()
  const { marketShareData } = useMarketShare({
    partnerId,
    fyear: partnerFYear,
    programType: buttonCurrType,
    section: 'jbp-gtm-priorities',
  })
  const isLoaded = isDataLoaded && gpDataLoadingStatus !== RequestStatus.PENDING

  /** -------------------- Variables ----------------------- */

  const isShipment = useMemo(() => {
    // return saleType === 'Shipment'
    return false
  }, [saleType])

  const isEmeaOnly = useMemo(() => {
    return !!partnerInfo?.IsGlobal && !!globalPartnerData?.isIncludeOnlyEmeaPartners
  }, [partnerInfo?.IsGlobal, globalPartnerData?.isIncludeOnlyEmeaPartners])

  const currentValueType = useMemo(() => {
    return !!partnerInfo?.IsGlobal && isEmeaOnly ? ActualsValueTypes.Ndp : actualsType
  }, [partnerInfo?.IsGlobal, isEmeaOnly, actualsType])

  const fyFq = PLANNING_BY_FY_FQ
  const currentCat = OVERALL_CATEGORY
  const catNAField = getCategoryNAField(currentCat)
  const isShowComments = false
  const fixedComposedReportFYear = props.composedReportFYear ?? jbpSettings.currentFYear
  const fyear = props.composedReportMode ? fixedComposedReportFYear : partnerFYear
  const isRetail = buttonCurrType === ProgramTypes.Retail
  const isNdp = currentValueType.toLowerCase() === 'ndp'
  const isAlwaysShowCategoryNA =
    buttonCurrType === ProgramTypes.MPS &&
    partnerInfo &&
    partnerInfo.Country === 'Germany' &&
    currentCat.includes('accessories')
  const isShowCategoryNA =
    !isAlwaysShowCategoryNA && !!props.formats.find((el) => !!el.ShowNotApplicable)
  // TotalPlanning widget use First Category
  const isShowCurrencyCtrl = !props.dataSetInfo.Settings?.HideCurrencyCtrl
  const isShowNetNdpCtrl = !props.dataSetInfo.Settings?.HideNetNdpCtrl
  const isShowActualsCtrl =
    !props.dataSetInfo.Settings?.HideActualsCtrl && !props.composedReportMode
  const isShowQuotaCtrl = false // !props.dataSetInfo.Settings?.HideQuotaCtrl && !props.composedReportMode
  const isShowSalesTypeCtrl =
    !props.dataSetInfo.Settings?.HideSalesTypeCtrl && !props.composedReportMode
  const isShowCtrlBeforeActuals = isShowCurrencyCtrl || isShowNetNdpCtrl
  const isEditAllowed =
    isEditMode && canEditPartner && (!isRegionalDistributor || props.isRegionalPlanning)

  const offeringName = props.fullTotalMode
    ? 'total'
    : buttonCurrType || props.dataSetInfo.ProgramType

  // Category title and tip keys
  const offeringNameLower = offeringName.replace(/\s/g, '').toLowerCase()
  const offeringKey = `jbp-planning-${offeringNameLower}`
  const globalCategoryKey = (props.globalCategory?.GlobalCategory || '').replace(/jbp-/g, '')
  const globalKey = !globalCategoryKey
    ? `jbp-${offeringNameLower}`
    : `jbp-${offeringNameLower}-${globalCategoryKey}`
  const totalKey = !partnerInfo?.IsGlobal ? offeringKey : `${globalKey}-total`
  const totalTipKey = `${totalKey}-tip`

  // Header key
  const headerSuffix = props.composedReportMode ? '-composed' : '-editor'
  const totalHeaderKey = `${totalKey}${headerSuffix}`

  const isShowContractualSwitcher =
    !!props.composedReportMode && !props.fullTotalMode && !!props.isContractualsAvailable
  const isShowOnlyCompletedPlansSwitcher =
    !!props.composedReportMode && !props.fullTotalMode && false

  const shortPrevFY = (fyear || 2000) - 2001
  const showPrevTargetsParams = [{ key: '{prevFY}', value: shortPrevFY.toString() }]

  const isShowFutureYears = props.isShowFutureYearsPlans && fyear > 2024

  const isUseContractuals = useMemo(() => {
    const flags = partnerInfo?.PartnerFlags || ''
    const isContrByFlag = isContractualsByFlag(flags, props.partnerFlagsState)

    return (
      buttonCurrType === ProgramTypes.CorePrint && (props.isContractualPlanning || isContrByFlag)
    )
  }, [
    buttonCurrType,
    props.isContractualPlanning,
    partnerInfo?.PartnerFlags,
    props.partnerFlagsState,
  ])

  const isShowAllSplitsCtrl = isUseContractuals && splitType === 'Total'
  const isShowTotal = !isUseContractuals || splitType === 'Total'
  const isShowContrTrans = isShowAllSplitsCtrl && isShowAllSplits
  const isShowContr =
    isUseContractuals &&
    ((!isShowAllSplitsCtrl && splitType !== 'Transactional') || isShowContrTrans)
  const isShowTrans =
    isUseContractuals && ((!isShowAllSplitsCtrl && splitType !== 'Contractual') || isShowContrTrans)
  const isShowOnlyCompletedPlans = !props.composedReportMode

  // Show PrevFY and Cals Actuals logic
  const isShowPrePreYear = IS_SHOW_PREV_PREV_FYEAR && !props.composedReportMode
  const isShowCalcActuals =
    IS_SHOW_PREV_RECALC_ACTUALS &&
    fyear > jbpSettings.fYear &&
    jbpSettings.currentFQuarter === 4 &&
    !props.composedReportMode
  const planningColSpan = useMemo(
    () => PLANNING_COL_NUMBER + (isShowPrePreYear ? 1 : 0) + (isShowCalcActuals ? 1 : 0),
    [isShowPrePreYear, isShowCalcActuals]
  )

  const isShowQuota = useMemo(
    () => props.showQuota && !partnerInfo?.IsGlobal,
    [props.showQuota, partnerInfo?.IsGlobal]
  )

  const sobCalcCategoriesParams = useMemo(() => {
    const sobCalcCategories = props.allDataSets
      .filter((el) => el.IsSoBTotalCalc)
      .map((el) => el.categoryTranslation)
      .join(', ')

    return [{ key: '{categories}', value: sobCalcCategories }]
  }, [props.formats])

  const isShowSoB = useMemo(() => {
    const isSobCategoriesExist = !!props.allDataSets.filter((el) => el.IsSoBTotalCalc).length
    const isNoSplitPlans = !isUseContractuals || splitType === 'Total'

    return (
      !props.composedReportMode &&
      isNoSplitPlans &&
      ((!!partnerInfo?.IsGlobal && !!props.globalCategory?.IsSoBAvailable) ||
        (!partnerInfo?.IsGlobal && isSobCategoriesExist))
    )
  }, [
    isUseContractuals,
    partnerInfo?.IsGlobal,
    props.allDataSets,
    props.composedReportMode,
    props.globalCategory?.IsSoBAvailable,
    splitType,
  ])

  /** -------------------- Methods ----------------------- */

  const isCategoryNA = () => {
    let isNotApplicable = !isAlwaysShowCategoryNA
    if (partnerInfo) {
      const sectionSetting = sectionSettings[catNAField]
      if (sectionSetting == undefined || sectionSetting == null) {
        // sectionSettings Not defined
        if (hideIfEmpty && !isDataPresent) {
          return true
        } else {
          // Not hideIfEmpty or dataPresent > use Original behave
          isNotApplicable = isNotApplicable && isSectionSettingOn(sectionSetting)
        }
      } else {
        // sectionSettings defined > use Original behave
        isNotApplicable = isNotApplicable && isSectionSettingOn(sectionSetting)
      }
    }

    return isNotApplicable && !props.composedReportMode
  }

  const isOn = (key: string, type: 'quarter' | 'sob' | 'units', defaultValue: boolean = false) => {
    let settingValue = defaultValue

    if (partnerInfo) {
      const field = getNAField(key, type)
      const val = sectionSettings[field]
      if (val !== undefined && val !== null) {
        settingValue = isSectionSettingOn(val)
      }
    }

    return settingValue
  }

  const preparePlanningData = () => {
    setIsDataLoaded(false)

    const isComposedReport = !!props.composedReportMode

    let filteredData = props.planningData.filter(
      (el) =>
        (el.ProgramTypes === buttonCurrType || isComposedReport) &&
        (!el.SubCategory || el.SubCategory === currentCat) &&
        !!el.IsTotal &&
        (!props.globalCategory || el.GlobalCategory === props.globalCategory.GlobalCategory)
      // operator: dataSetInfo.SummaryProgram ? Operator.CONTAINS : Operator.EQUAL,
      // value: dataSetInfo.category.replace('-overall', ''),
    )

    if (!isComposedReport) {
      // Show distributor's group data for Regional Distributor:
      // Get data from all Partner in group, Except Regional Distributor
      if (isRegionalDistributor && !props.isRegionalPlanning) {
        filteredData = filteredData.filter((el) => el.PartnerId !== partnerId)
      } else {
        filteredData = filteredData.filter((el) => el.PartnerId === partnerId)
      }
    }

    const rawTargets = filteredData.filter(
      (el) =>
        !!el.IsTarget && (!isComposedReport || !el.IsNoSplit) && !el.IsRollUp && !el.IsPartnerCount
    )
    const rawActuals = filteredData.filter(
      (el) =>
        !!el.IsActuals && (!isComposedReport || !el.IsNoSplit) && !el.IsRollUp && !el.IsPartnerCount
    )
    const rawTargetsRollup = filteredData.filter((el) => !!el.IsTarget && !!el.IsRollUp)
    const rawActualsRollup = filteredData.filter((el) => !!el.IsActuals && !!el.IsRollUp)

    const futureYears = isShowFutureYears ? 2 : 0
    const lastFYear = fyear + futureYears

    const commonParams: IPreparePlanningItems = {
      planningSettings: props.formats,
      source: rawTargets,
      partnerId: partnerId,
      fyear: fyear,
      lastFYear: lastFYear,
      isActuals: 0,
      isDirect: 0,
      isTarget: 1,
      isQuota: 0,
      isComposedReport: isComposedReport,
      isNoSplit: 0,
      isRollUp: 0,
      isTotal: 1,
      isPartnerCount: 0,
      globalCategory: globalCategoryKey,
    }

    const targetsItems = preparePlanningItems(commonParams)
    const actualsItems = preparePlanningItems({
      ...commonParams,
      source: rawActuals,
      isTarget: 0,
      isActuals: 1,
    })
    const targetsRollupItems = preparePlanningItems({
      ...commonParams,
      source: rawTargetsRollup,
      isRollUp: 1,
    })
    const actualsRollupItems = preparePlanningItems({
      ...commonParams,
      source: rawActualsRollup,
      isTarget: 0,
      isActuals: 1,
      isRollUp: 1,
    })

    setTargetsItems(targetsItems)
    setActualsItems(actualsItems)
    setTargetsRollupItems(targetsRollupItems)
    setActualsRollupItems(actualsRollupItems)

    if (rawTargets.length) {
      const isCollapsedCategory = getIsCollapsedCategory(targetsItems, fyear)
      setIsCollapsed(isCollapsedCategory)
    }

    // Begin: calculate hideIfEmpty, dataNotEmpty
    let hideEmptyData: boolean = false
    if (props.formats) {
      props.formats.map((el) => {
        if (!!el.HideIfEmpty && !hideIfEmpty) {
          setHideIfEmpty(true)
          hideEmptyData = true
        }
      })
    }

    if (hideEmptyData) {
      let isDataPresent = false
      targetsItems.map((el) => {
        if (el.IsDataPresent) {
          isDataPresent = true
        }
      })
      setIsDataPresent(isDataPresent)
    }
    // End: calculate hideIfEmpty, dataNotEmpty

    setIsDataLoaded(true)
  }

  const getComment = (settingsIndex: number, fy: number, fq: number): string => {
    const items = targetsItems

    if (settingsIndex < props.formats.length) {
      const setting = props.formats[settingsIndex]
      return getCommentInternal(setting, items, fy, fq)
    } else {
      return ''
    }
  }

  const getPopupComment = (settingsIndex: number): string => {
    return settingsIndex < props.formats.length ? props.formats[settingsIndex].PopupComment : ''
  }

  const getTotalValue = (
    settingsIndex: number,
    type: 'actuals' | 'targets',
    fy: number,
    fq: number,
    isUnits: boolean,
    isContractual: boolean,
    isSoB: boolean,
    isRollUp: boolean
  ): number => {
    let items = targetsItems
    if (type === 'actuals') {
      items = actualsItems
    }
    if (isRollUp) {
      if (type === 'actuals') {
        items = actualsRollupItems
      } else {
        items = targetsRollupItems
      }
    }

    if (settingsIndex < props.formats.length) {
      const setting = props.formats[settingsIndex]
      return getTotalValueInternal(
        setting,
        items,
        fy,
        fq,
        isNdp,
        isUnits,
        isShipment,
        currency,
        isContractual,
        isSoB
      )
    } else {
      return 0
    }
  }

  const getCalcActuals = (
    settingsIndex: number,
    fy: number,
    isUnits: boolean,
    isContractual: boolean,
    isSoB: boolean
  ): number => {
    const actualQ1 = getTotalValue(
      settingsIndex,
      'actuals',
      fy,
      1,
      isUnits,
      isContractual,
      isSoB,
      false
    )
    const actualQ2 = getTotalValue(
      settingsIndex,
      'actuals',
      fy,
      2,
      isUnits,
      isContractual,
      isSoB,
      false
    )
    const actualQ3 = getTotalValue(
      settingsIndex,
      'actuals',
      fy,
      3,
      isUnits,
      isContractual,
      isSoB,
      false
    )
    const targetQ4 = getTotalValue(
      settingsIndex,
      'targets',
      fy,
      4,
      isUnits,
      isContractual,
      isSoB,
      false
    )

    return (actualQ1 || 0) + (actualQ2 || 0) + (actualQ3 || 0) + (targetQ4 || 0)
  }

  const getTotalValueInternal = (
    setting: IFormActualsTargetsSettings,
    items: IJBPPlanningItem[],
    fy: number,
    fq: number,
    isNdp: boolean,
    isUnits: boolean,
    isShipment: boolean,
    currency: string,
    isContractual: boolean,
    isSoB: boolean
  ): number => {
    let result = 0

    // Other sales types: SellIn, SellOut, SellTo
    const contrStr = isContractual ? 'Contractual' : ''
    let saleStr = isShipment ? 'Shipment' : 'SellThru'
    if (isContractual) {
      saleStr = ''
    }
    const unitStr = isUnits ? 'Units' : 'Sum'
    let currStr = !currency || currency === 'USD' ? 'USD' : 'LC'
    let ndpStr = isNdp ? 'Ndp' : 'Net'
    if (isUnits) {
      currStr = ''
      if (!isNdp) {
        ndpStr = ''
      }
    }
    let fieldName = `${contrStr}${saleStr}${unitStr}${currStr}${ndpStr}`
    if (isSoB) {
      fieldName = 'SoB'
    }

    // Items filtered on Top level by: PartnerId, IsActuals, IsTarget, IsQuota
    const sourceData = items.filter(
      (el) =>
        (el.ProgramTypes === setting.ProgramType || props.fullTotalMode) &&
        // For all categories
        //el.Category === setting.Category &&
        // Overall only
        (!el.SubCategory || el.SubCategory === OVERALL_CATEGORY) &&
        el.FYear === fy &&
        el.FQuarter === fq
    )

    // Sum variant
    //sourceData.forEach((el) => { result += Number(el[fieldName] || 0) })

    // Without Sum variant
    if (sourceData.length >= 1) {
      const value = sourceData[0][fieldName]
      result = value === undefined ? null : value
    } else {
      // error in data
    }

    return result
  }

  const getCommentInternal = (
    setting: IFormActualsTargetsSettings,
    items: IJBPPlanningItem[],
    fy: number,
    fq: number
  ): string => {
    let result = ''

    // Items filtered on Top level by: PartnerId, IsActuals, IsTarget
    const sourceData = items.filter(
      (el) =>
        el.ProgramTypes === setting.ProgramType &&
        el.Category === setting.Category &&
        el.SubCategory === setting.SubCategory &&
        el.FYear === fy &&
        el.FQuarter === fq
    )
    if (sourceData.length >= 1) {
      result = sourceData[0].Comment ?? ''
    } else {
      // error in data
    }

    return result
  }

  /** -------------------- User actions handlers ----------------------- */

  /** -------------------- Render methods ----------------------- */

  const fieldTotalWrapper = (
    fieldValue: number,
    year: number,
    fieldType: 'targets' | 'actuals' | 'quota' = 'targets'
  ) => {
    let formattedValue = formatMoneyInteger(fieldValue)
    if (formattedValue === '0') {
      formattedValue = '-'
    }

    let isUseColor = false
    let color = targetsColor
    switch (fieldType) {
      case 'actuals': {
        color = actualsColor
        isUseColor = true
        break
      }
      case 'quota': {
        color = quotaColor
        isUseColor = true
        break
      }
      case 'targets':
      default: {
        // Not use custom Color
        break
      }
    }

    if (!isRetail) {
      return !isUseColor ? formattedValue : <div style={{ color: color }}>{formattedValue}</div>
    } else {
      return (
        <>{!isUseColor ? formattedValue : <div style={{ color: color }}>{formattedValue}</div>}</>
      )
    }
  }

  const renderQ = (
    idx: number,
    q: number,
    isEditAllowed: boolean,
    isUnits: boolean,
    isRollUp: boolean
  ) => {
    const isPrevYear = q < 0
    q = Math.abs(q)
    const isPlanningByFY = q >= PLANNING_BY_FY_FQ
    let futureYears = 0
    if (q > PLANNING_BY_FY_FQ) {
      futureYears = q - PLANNING_BY_FY_FQ
      q = PLANNING_BY_FY_FQ
    }
    const year = fyear - (isPrevYear ? 1 : 0) + futureYears
    const isSoB = false
    const isContr = false

    const isShowPrevFY = !isPlanningByFY || (isShowPrePreYear && isPrevYear)
    const isShowPrevCalc = isPlanningByFY && isPrevYear
    const isShowPrevCalcActuals = isShowPrevCalc && isShowCalcActuals
    const isUseCalcActualsForYoY = isPlanningByFY && !isPrevYear && isShowCalcActuals

    const preActuals = getTotalValue(idx, 'actuals', year - 1, q, isUnits, isContr, isSoB, false)
    const currActuals = getTotalValue(idx, 'actuals', year, q, isUnits, isContr, isSoB, false)
    const preTargets = getTotalValue(idx, 'targets', year - 1, q, isUnits, isContr, isSoB, isRollUp)
    const currTargets = getTotalValue(idx, 'targets', year, q, isUnits, isContr, isSoB, isRollUp)
    const comment = getComment(idx, year, q)

    const calActualsFy = isUseCalcActualsForYoY ? year - 1 : year
    const preActualsCalc = getCalcActuals(idx, calActualsFy, isUnits, isContr, isSoB)

    const yoyTargets = calcPercentage(preActuals, currTargets)
    const yoyActuals = calcPercentage(preActuals, currActuals)

    const isQuarterPlanning = isOn(currentCat, 'quarter', IS_QUARTER_DEFAULT)
    const isActiveColumns =
      (isQuarterPlanning && !isPlanningByFY) || (!isQuarterPlanning && isPlanningByFY)

    const isEditAllowedForCollapsed = isEditAllowed && isCollapsed
    const isEditByQuarter = isEditAllowedForCollapsed && isActiveColumns && false

    const isHidePrevTargets = !isShowPrevTargets
    const isHideCurrTargets = isHidePrevTargets && isPrevYear

    const isShowActualsCell = props.showActuals && !isRollUp
    const isShowQuota = props.showQuota && !isRollUp
    const isStripCol = !isActiveColumns && !isRollUp

    return (
      <>
        {isShowPrevFY && (
          <td
            className={cn(styles.colSeparator, {
              [styles.strip]: isStripCol,
              [styles.rollUp]: isRollUp,
            })}
          >
            <div className={styles.itemWrapper}>
              <div className={styles.itemValues}>
                <div className={styles.itemQuota}></div>
                <div className={styles.itemActual}>
                  {isShowActualsCell && fieldTotalWrapper(preActuals, year - 1, 'actuals')}
                </div>
                <div className={styles.itemTarget}>
                  {isHidePrevTargets ? <>&nbsp;</> : <>{fieldTotalWrapper(preTargets, year - 1)}</>}
                </div>
              </div>
            </div>
          </td>
        )}
        {isShowPrevCalcActuals && (
          <td
            className={cn({
              [styles.colStrip]: isStripCol,
              [styles.colSeparator]: isPlanningByFY,
              [styles.rollUp]: isRollUp,
            })}
          >
            <div className={styles.itemWrapper}>
              <div className={styles.itemValues}>
                <div className={styles.itemQuota}>{isShowQuota && <>&nbsp;</>}</div>
                <div className={styles.itemActual}>
                  {isShowActualsCell && fieldTotalWrapper(preActualsCalc, year - 1, 'actuals')}
                </div>
                <div className={cn(styles.itemTarget)}>
                  {isHideCurrTargets ? (
                    <>&nbsp;</>
                  ) : (
                    <>{fieldTotalWrapper(currTargets, year, 'targets')}</>
                  )}
                </div>
              </div>
            </div>
          </td>
        )}
        <td
          className={cn({
            [styles.strip]: isStripCol,
            [styles.colSeparator]: isPlanningByFY && !isShowPrevCalcActuals,
            [styles.colSeparatorThin]: isPlanningByFY && isShowPrevCalcActuals,
            [styles.rollUp]: isRollUp,
          })}
        >
          <div
            className={cn(styles.itemWrapper, {
              [styles.itemOverall]: !(isShowActualsCell || isShowQuota) && !isEditByQuarter,
            })}
          >
            <div className={styles.itemValues}>
              <div className={styles.itemQuota}></div>
              <div className={styles.itemActual}>
                {isShowActualsCell && fieldTotalWrapper(currActuals, year, 'actuals')}
              </div>
              <div className={styles.itemTarget}>
                {!isEditByQuarter && isShowComments && <CommentIcon tooltipText={comment} />}
                {isHideCurrTargets ? <>&nbsp;</> : <>{fieldTotalWrapper(currTargets, year)}</>}
              </div>
            </div>
          </div>
        </td>
        <td className={cn({ [styles.strip]: isStripCol, [styles.rollUp]: isRollUp })}>
          <div className={styles.itemWrapper}>
            <div className={cn(styles.itemIcon, { [styles.itemEditIcon]: isEditByQuarter })}>
              {isShowActualsCell && <GrowField percent={yoyActuals} isPaleColor={true} />}
              {isHideCurrTargets ? <span>&nbsp;</span> : <GrowField percent={yoyTargets} />}
            </div>
          </div>
        </td>
      </>
    )
  }

  const renderQContr = (
    idx: number,
    q: number,
    isEditAllowed: boolean,
    isUnits: boolean,
    line: 'c' | 't'
  ) => {
    const isPrevYear = q < 0
    q = Math.abs(q)
    const isPlanningByFY = q >= PLANNING_BY_FY_FQ
    let futureYears = 0
    if (q > PLANNING_BY_FY_FQ) {
      futureYears = q - PLANNING_BY_FY_FQ
      q = PLANNING_BY_FY_FQ
    }
    const year = fyear - (isPrevYear ? 1 : 0) + futureYears
    const isSoB = false
    const isRollUp = false

    const isShowPrevFY = !isPlanningByFY || (isShowPrePreYear && isPrevYear)
    const isShowPrevCalc = isPlanningByFY && isPrevYear
    const isShowPrevCalcActuals = isShowPrevCalc && isShowCalcActuals
    const isUseCalcActualsForYoY = isPlanningByFY && !isPrevYear && isShowCalcActuals

    const preActualsTotal = getTotalValue(idx, 'actuals', year - 1, q, isUnits, false, isSoB, false)
    const currActualsTotal = getTotalValue(idx, 'actuals', year, q, isUnits, false, isSoB, false)
    const preTargetsTotal = getTotalValue(idx, 'targets', year - 1, q, isUnits, false, isSoB, false)
    const currTargetsTotal = getTotalValue(idx, 'targets', year, q, isUnits, false, isSoB, false)

    const preActualsContr = getTotalValue(idx, 'actuals', year - 1, q, isUnits, true, isSoB, false)
    const currActualsContr = getTotalValue(idx, 'actuals', year, q, isUnits, true, isSoB, false)
    const preTargetsContr = getTotalValue(idx, 'targets', year - 1, q, isUnits, true, isSoB, false)
    const currTargetsContr = getTotalValue(idx, 'targets', year, q, isUnits, true, isSoB, false)

    const calActualsFy = isUseCalcActualsForYoY ? year - 1 : year
    const preActualsTotalCalc = getCalcActuals(idx, calActualsFy, isUnits, false, isSoB) // Total
    const preActualsContrCalc = getCalcActuals(idx, calActualsFy, isUnits, true, isSoB) // Contractual
    const preActualsTranCalc = Number(preActualsTotalCalc || 0) - Number(preActualsContrCalc || 0)

    const preActualsTran = Number(preActualsTotal || 0) - Number(preActualsContr || 0)
    const currActualsTran = Number(currActualsTotal || 0) - Number(currActualsContr || 0)
    const preTargetsTran = Number(preTargetsTotal || 0) - Number(preTargetsContr || 0)
    const currTargetsTran = Number(currTargetsTotal || 0) - Number(currTargetsContr || 0)

    const yoyActualsTran = calcPercentage(preActualsTran, currActualsTran)
    const yoyActualsContr = calcPercentage(preActualsContr, currActualsContr)
    const yoyTargetsTran = calcPercentage(preActualsTran, currTargetsTran)
    const yoyTargetsContr = calcPercentage(preActualsContr, currTargetsContr)

    const isOverall = true

    const isQuarterPlanning = isOn(currentCat, 'quarter', IS_QUARTER_DEFAULT)
    const isActiveColumns =
      (isQuarterPlanning && !isPlanningByFY) || (!isQuarterPlanning && isPlanningByFY)
    const isEditCells = false

    const isShowActualsCell = props.showActuals && !isRollUp
    const isItemOverall = isOverall && !(props.showActuals || isShowQuota) && !isEditCells

    const isC = line === 'c'
    const isT = line === 't'

    const isHidePrevTargets = !isShowPrevTargets
    const isHideCurrTargets = isHidePrevTargets && isPrevYear

    return (
      <>
        {isShowPrevFY && (
          <td className={cn(styles.colSeparator, { [styles.strip]: !isActiveColumns })}>
            <div className={styles.itemWrapper}>
              <div className={styles.itemValues}>
                {isC && (
                  <>
                    <div className={styles.itemActual}>
                      {isShowActualsCell && fieldTotalWrapper(preActualsContr, year - 1, 'actuals')}
                    </div>
                    <div className={styles.itemTarget}>
                      {isHidePrevTargets ? (
                        <>&nbsp;</>
                      ) : (
                        <>{fieldTotalWrapper(preTargetsContr, year - 1)}</>
                      )}
                    </div>
                  </>
                )}
                {isT && (
                  <>
                    <div className={styles.itemActual}>
                      {isShowActualsCell && fieldTotalWrapper(preActualsTran, year - 1, 'actuals')}
                    </div>
                    <div className={styles.itemTarget}>
                      {isHidePrevTargets ? (
                        <>&nbsp;</>
                      ) : (
                        <>{fieldTotalWrapper(preTargetsTran, year - 1)}</>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </td>
        )}
        {isShowPrevCalcActuals && (
          <td
            className={cn({
              [styles.colStrip]: !isActiveColumns,
              [styles.colSeparator]: isPlanningByFY,
            })}
          >
            <div className={styles.itemWrapper}>
              <div className={styles.itemValues}>
                {isC && (
                  <>
                    <div className={styles.itemQuota}>{isShowQuota && <>&nbsp;</>}</div>
                    <div className={styles.itemActual}>
                      {fieldTotalWrapper(preActualsContrCalc, year - 1, 'actuals')}
                    </div>
                    <div className={cn(styles.itemTarget)}>
                      {isHideCurrTargets ? (
                        <>&nbsp;</>
                      ) : (
                        <>{fieldTotalWrapper(currTargetsContr, year, 'targets')}</>
                      )}
                    </div>
                  </>
                )}
                {isT && (
                  <>
                    <div className={styles.itemQuota}>{isShowQuota && <>&nbsp;</>}</div>
                    <div className={styles.itemActual}>
                      {fieldTotalWrapper(preActualsTranCalc, year - 1, 'actuals')}
                    </div>
                    <div className={cn(styles.itemTarget)}>
                      {isHideCurrTargets ? (
                        <>&nbsp;</>
                      ) : (
                        <>{fieldTotalWrapper(currTargetsTran, year, 'targets')}</>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </td>
        )}
        <td
          className={cn({
            [styles.strip]: !isActiveColumns,
            [styles.colSeparator]: isPlanningByFY && !isShowPrevCalcActuals,
            [styles.colSeparatorThin]: isPlanningByFY && isShowPrevCalcActuals,
          })}
        >
          <div className={cn(styles.itemWrapper, { [styles.itemOverall]: isItemOverall })}>
            <div className={styles.itemValues}>
              {isC && (
                <>
                  {isShowActualsCell && (
                    <div className={styles.itemActual}>
                      {isShowActualsCell && fieldTotalWrapper(currActualsContr, year, 'actuals')}
                    </div>
                  )}
                  <div className={styles.itemTarget}>
                    {isHideCurrTargets ? (
                      <>&nbsp;</>
                    ) : (
                      <>{fieldTotalWrapper(currTargetsContr, year)}</>
                    )}
                  </div>
                </>
              )}
              {isT && (
                <>
                  {isShowActualsCell && (
                    <div className={styles.itemActual}>
                      {isShowActualsCell && fieldTotalWrapper(currActualsTran, year, 'actuals')}
                    </div>
                  )}
                  <div className={styles.itemTarget}>
                    {isHideCurrTargets ? (
                      <>&nbsp;</>
                    ) : (
                      <>{fieldTotalWrapper(currTargetsTran, year - 1)}</>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </td>
        <td className={cn({ [styles.strip]: !isActiveColumns })}>
          <div className={styles.itemWrapper}>
            <div
              className={cn(
                styles.itemIcon,
                { [styles.itemOneIcon]: !isShowActualsCell },
                { [styles.itemEditIcon]: isEditCells }
              )}
            >
              {isC && (
                <>
                  {isShowActualsCell && <GrowField percent={yoyActualsContr} isPaleColor={true} />}
                  <GrowField percent={yoyTargetsContr} />
                </>
              )}
              {isT && (
                <>
                  {isShowActualsCell && <GrowField percent={yoyActualsTran} isPaleColor={true} />}
                  <div className={cn({ [styles.itemTwoIcon]: isEditCells })}>
                    <GrowField percent={yoyTargetsTran} />
                  </div>
                </>
              )}
            </div>
          </div>
        </td>
      </>
    )
  }

  const renderSoB = (
    idx: number,
    isOverall: boolean,
    q: number,
    isEditAllowed: boolean,
    isUnits: boolean,
    isNoPlan: boolean,
    isRollUp: boolean
  ) => {
    const isPrevYear = q < 0
    q = Math.abs(q)
    const isPlanningByFY = q >= PLANNING_BY_FY_FQ
    let futureYears = 0
    if (q > PLANNING_BY_FY_FQ) {
      futureYears = q - PLANNING_BY_FY_FQ
      q = PLANNING_BY_FY_FQ
    }
    const year = fyear - (isPrevYear ? 1 : 0) + futureYears

    const isShowPrevFY = !isPlanningByFY || (isShowPrePreYear && isPrevYear)
    const isShowPrevCalc = isPlanningByFY && isPrevYear
    const isShowPrevCalcActuals = isShowPrevCalc && isShowCalcActuals

    const isShowActualsCell = props.showActuals && !isRollUp

    const isSoB = true
    const isContr = false

    const preActual = getTotalValue(idx, 'actuals', year - 1, q, isUnits, isContr, isSoB, isRollUp)
    const preTarget = getTotalValue(idx, 'targets', year - 1, q, isUnits, isContr, isSoB, isRollUp)
    const curActual = getTotalValue(idx, 'actuals', year, q, isUnits, isContr, isSoB, isRollUp)
    const curTarget = getTotalValue(idx, 'targets', year, q, isUnits, isContr, isSoB, isRollUp)

    // TODO: is CompletenessItem need for RollUp?
    const preActCompleteness = getCompletenessItem({
      formats: props.formats,
      settingsIndex: idx,
      items: actualsItems,
      fy: year - 1,
      isSoB: true,
    })
    const preTarCompleteness = getCompletenessItem({
      formats: props.formats,
      settingsIndex: idx,
      items: targetsItems,
      fy: year - 1,
      isSoB: true,
    })
    const curActCompleteness = getCompletenessItem({
      formats: props.formats,
      settingsIndex: idx,
      items: actualsItems,
      fy: year,
      isSoB: true,
    })
    const curTarCompleteness = getCompletenessItem({
      formats: props.formats,
      settingsIndex: idx,
      items: targetsItems,
      fy: year,
      isSoB: true,
    })

    const yoyTarget = calcDiff(preActual, curTarget)
    const yoyActual = calcDiff(preActual, curActual)

    const isActiveColumns = true

    const setting = props.formats[idx]

    const flags = partnerInfo?.PartnerFlags || ''
    const isMandatoryByFlag = isSubCategoryMandatory(
      flags,
      setting.SOBMandatoryByFlags,
      sectionSettings,
      false
    )
    const isShowMandatory =
      isMandatoryByFlag.isMandatory && !isPrevYear && !isUnits && !props.composedReportMode

    const isShowZero = !IS_SHOW_NULL && isShowMandatory && Number(curTarget || 0) <= 0

    const isShowPreActCompletenessField =
      !!props.composedReportMode && !!preActCompleteness.partnersNotCompleted
    const isShowPreTarCompletenessField =
      !!props.composedReportMode && !!preTarCompleteness.partnersNotCompleted
    const isShowCurActCompletenessField =
      !!props.composedReportMode && !!curActCompleteness.partnersNotCompleted
    const isShowCurTarCompletenessField =
      !!props.composedReportMode && !!curTarCompleteness.partnersNotCompleted

    const onSoBCompletenessClick = (partnersCompleteness: IPartnersCompleteness) => {}

    const completenessTag = isShowOnlyCompletedPlans ? 'sobAck' : 'sob'

    const isHidePrevTargets = !isShowPrevTargets
    const isHideCurrTargets = isHidePrevTargets && isPrevYear
    const isHideCurrTargetsYoY = isHideCurrTargets || (isNoPlan && !isPrevYear)

    return (
      <>
        {isShowPrevFY && (
          <td
            className={cn(styles.colSeparator, {
              [styles.colStrip]: !isActiveColumns,
              [styles.rollUp]: isRollUp,
            })}
          >
            <div className={styles.itemWrapper}>
              <div className={styles.itemValues}>
                <div className={styles.itemActual}>
                  {isShowActualsCell && (
                    <>
                      {isShowPreActCompletenessField ? (
                        <PartnersCompletenessField
                          translationTag={completenessTag}
                          partnersCompleteness={preActCompleteness}
                          onClick={onSoBCompletenessClick}
                          type="actuals"
                        />
                      ) : (
                        fieldWrapper(
                          preActual,
                          year - 1,
                          isRetail,
                          marketShareData,
                          'actuals',
                          isShowZero,
                          SOB_FRACTION_DIGITS
                        )
                      )}
                    </>
                  )}
                </div>
                <div className={styles.itemTarget}>
                  {isShowPreTarCompletenessField ? (
                    <PartnersCompletenessField
                      translationTag={completenessTag}
                      partnersCompleteness={preTarCompleteness}
                      onClick={onSoBCompletenessClick}
                      type="targets"
                    />
                  ) : (
                    <>
                      {isHidePrevTargets ? (
                        <>&nbsp;</>
                      ) : (
                        fieldWrapper(
                          preTarget,
                          year - 1,
                          isRetail,
                          marketShareData,
                          'targets',
                          isShowZero,
                          SOB_FRACTION_DIGITS
                        )
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </td>
        )}
        {isShowPrevCalcActuals && (
          <td
            className={cn({
              [styles.colStrip]: !isActiveColumns,
              [styles.colSeparator]: isPlanningByFY,
              [styles.rollUp]: isRollUp,
            })}
          >
            <div className={styles.itemWrapper}>
              <div className={styles.itemValues}>
                <div className={cn(styles.itemActual, styles.itemTarget)}>&nbsp;</div>
                <div className={styles.itemTarget}>&nbsp;</div>
              </div>
            </div>
          </td>
        )}
        <td
          className={cn({
            [styles.colStrip]: !isActiveColumns,
            [styles.colSeparator]: isPlanningByFY && !isShowPrevCalcActuals,
            [styles.colSeparatorThin]: isPlanningByFY && isShowPrevCalcActuals,
            [styles.rollUp]: isRollUp,
          })}
        >
          <div className={styles.itemWrapper}>
            <div className={styles.itemValues}>
              <div className={cn(styles.itemActual, styles.itemTarget)}>
                {isShowActualsCell && (
                  <>
                    {isShowCurActCompletenessField ? (
                      <PartnersCompletenessField
                        translationTag={completenessTag}
                        partnersCompleteness={curActCompleteness}
                        onClick={onSoBCompletenessClick}
                        type="actuals"
                      />
                    ) : (
                      fieldWrapper(
                        curActual,
                        year,
                        isRetail,
                        marketShareData,
                        'actuals',
                        isShowZero,
                        SOB_FRACTION_DIGITS
                      )
                    )}
                  </>
                )}
              </div>
              <div className={styles.itemTarget}>
                {isNoPlan && !isPrevYear ? (
                  <Translation text="jbp-planning-no-plan" span />
                ) : (
                  <>
                    {isShowCurTarCompletenessField ? (
                      <PartnersCompletenessField
                        translationTag={completenessTag}
                        partnersCompleteness={curTarCompleteness}
                        onClick={onSoBCompletenessClick}
                        type="targets"
                      />
                    ) : (
                      <>
                        <>
                          {isHideCurrTargets ? (
                            <>&nbsp;</>
                          ) : (
                            fieldWrapper(
                              curTarget,
                              year,
                              isRetail,
                              marketShareData,
                              'targets',
                              isShowZero,
                              SOB_FRACTION_DIGITS
                            )
                          )}
                        </>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </td>
        <td className={cn({ [styles.colStrip]: !isActiveColumns, [styles.rollUp]: isRollUp })}>
          <div className={styles.itemWrapper}>
            <div className={styles.itemIcon}>
              {isShowActualsCell && (
                <>
                  {isShowCurActCompletenessField ? (
                    <>-</>
                  ) : (
                    <>
                      <GrowField
                        percent={yoyActual}
                        fractionDigits={SOB_FRACTION_DIGITS}
                        isPaleColor={true}
                        diff
                      />
                    </>
                  )}
                </>
              )}
              <div>
                {isHideCurrTargetsYoY ? (
                  <span>&nbsp;</span>
                ) : (
                  <>
                    {isShowCurTarCompletenessField ? (
                      <>-</>
                    ) : (
                      <>
                        <GrowField percent={yoyTarget} fractionDigits={SOB_FRACTION_DIGITS} diff />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </td>
      </>
    )
  }

  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    if (!props.urlActualsValueType) return

    const urlTypeLower = props.urlActualsValueType.toLowerCase()
    const urlType = Object.values(ActualsValueTypes).find((v) => v.toLowerCase() === urlTypeLower)
    if (!urlType || !actualsTypes.includes(urlType)) return

    onChangeActualsType(urlType)
  }, [props.urlActualsValueType, actualsTypes, onChangeActualsType])

  useEffect(() => {
    if ((partnerId && fyear && buttonCurrType) || props.composedReportMode) {
      preparePlanningData()
    }
  }, [
    partnerId,
    fyear,
    buttonCurrType,
    //props.dataSetInfo,
    isRegionalDistributor,
    props.isRegionalPlanning,
    props.planningData,
    props.globalCategory,
  ])

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.root}>
      <table className={cn(stylesTable.table, stylesCompensation.table, styles.table)}>
        <caption>
          <WidgetHeaderWithHelpButton section={HelpSection.FinancialAndSOB}>
            {totalHeaderKey}
          </WidgetHeaderWithHelpButton>
          <LoaderOverlay show={!isLoaded} />

          {isShowCategoryNA && !isEditAllowed && isCategoryNA() && (
            <tr>
              <th colSpan={planningColSpan}>
                <Translation text={'jbp-not-applicable-status-not-applicable'} />
              </th>
            </tr>
          )}
        </caption>

        <thead>
          {!isCategoryNA() && (
            <tr>
              <th colSpan={planningColSpan}>
                <div className={styles.filtersContainer}>
                  <div className={styles.filtersRow}>
                    {isShowCurrencyCtrl && (
                      <>
                        <CurrenciesButtons />
                        <div className={styles.filterNextItemEmpty} />
                      </>
                    )}
                    {isShowNetNdpCtrl && (
                      <>
                        <ActualsTypeButtons />
                        <div className={styles.filterNextItemEmpty} />
                      </>
                    )}
                    {isShowSalesTypeCtrl && (
                      <>
                        <SellTypeButtons actualsType={actualsType} />
                        <div className={styles.filterNextItemEmpty} />
                      </>
                    )}
                    {isShowActualsCtrl && !IS_ALWAYS_SHOW_ACTUALS && (
                      <>
                        <div className={cn(styles.filterItem)} style={{ color: actualsColor }}>
                          <Checkbox
                            checked={props.showActuals}
                            label={<Translation text={'jbp-partner-actuals-show-actuals'} span />}
                            onChange={(val) => props.setShowActuals(!!val)}
                          />
                        </div>
                        <div className={styles.filterNextItemEmpty} />
                      </>
                    )}
                    {isShowQuotaCtrl && (
                      <>
                        <div className={cn(styles.filterItem)} style={{ color: quotaColor }}>
                          <Checkbox
                            checked={isShowQuota}
                            label={<Translation text={'jbp-partner-actuals-show-quota'} span />}
                            onChange={(val) => props.setShowQuota(!!val)}
                          />
                        </div>
                        <div className={styles.filterNextItemEmpty} />
                      </>
                    )}
                    {isDistributor && isRegionalDistributor && (
                      <Checkbox
                        className={cn(styles.filterItem)}
                        checked={props.isRegionalPlanning}
                        label={<Translation text={'jbp-distributor-show-regional-planning'} span />}
                        onChange={(val) => props.setIsRegionalPlanning(!!val)}
                      />
                    )}
                    <div className={styles.switchersContainer}>
                      {isShowContractualSwitcher && (
                        <div className={styles.switcher}>
                          <Switch
                            padding
                            checked={isUseContractuals}
                            onChange={() =>
                              props.setIsContractualPlanning &&
                              props.setIsContractualPlanning(!isUseContractuals)
                            }
                          />
                          <div className={styles.switcherText}>
                            <Translation span text="jbp-planning-contractual-switcher" />
                          </div>
                        </div>
                      )}
                      {isShowOnlyCompletedPlansSwitcher && (
                        <div className={styles.switcher}>
                          <Switch
                            padding
                            checked={props.isShowOnlyCompletedPlans}
                            onChange={() =>
                              props.setIsShowOnlyCompletedPlans &&
                              props.setIsShowOnlyCompletedPlans(!props.isShowOnlyCompletedPlans)
                            }
                          />
                          <div className={styles.switcherText}>
                            <Translation span text="jbp-planning-show-only-completed-plans" />
                          </div>
                        </div>
                      )}
                      <div className={cn(styles.filterItem)}>
                        <Checkbox
                          checked={isShowPrevTargets}
                          onChange={() => setIsShowPrevTargets((val) => !val)}
                          label={
                            <Translation
                              span
                              text="jbp-planning-show-prev-targets"
                              replaceParams={showPrevTargetsParams}
                            />
                          }
                        />
                      </div>
                      {isShowAllSplitsCtrl && (
                        <div className={styles.filterItem}>
                          <Checkbox
                            checked={isShowAllSplits}
                            onChange={() => setIsShowAllSplits((val) => !val)}
                            label={
                              <Translation text={'jbp-planning-show-contr-trans-switcher'} span />
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </th>
            </tr>
          )}

          {!isCategoryNA() && (
            <>
              <tr>
                <th colSpan={planningColSpan}>
                  {props.showActuals && (
                    <div className={styles.infoContainer}>
                      <i
                        className={cn('kbase kbase-info-alt', styles.infoIcon)}
                        aria-hidden="true"
                      />
                      <span className={styles.actualsLegend}>
                        <Translation text="jbp-planning-actuals-legend" actionsInside />
                      </span>
                      <span className={styles.targetsLegend}>
                        <Translation text="jbp-planning-targets-legend" actionsInside />
                      </span>
                    </div>
                  )}
                  <div className={styles.infoContainer}>
                    {!props.showActuals && (
                      <i
                        className={cn('kbase kbase-info-alt', styles.infoIcon)}
                        aria-hidden="true"
                      />
                    )}
                    <Translation
                      text={
                        props.showActuals
                          ? 'jbp-planning-targets-info'
                          : 'jbp-planning-actuals-info'
                      }
                      actionsInside
                    />
                  </div>
                </th>
              </tr>
              {isUseContractuals && (
                <tr className={styles.splitPlanningRow}>
                  <th colSpan={19}>
                    <SplitPlanningSwitcher splitType={splitType} setSplitType={setSplitType} />
                  </th>
                </tr>
              )}
              {!isCategoryNA() && (
                <CategoryHeaderStandard
                  translationKey="jbp-planning-total-header"
                  fyear={fyear}
                  isShowCalcActuals={isShowCalcActuals}
                  isShowFutureYears={isShowFutureYears}
                  currentFQuarter={jbpSettings.currentFQuarter}
                  isComposedReport={!!props.composedReportMode}
                />
              )}
            </>
          )}
        </thead>

        {!isCategoryNA() && isDataLoaded && (
          <tbody>
            {[OVERALL_CATEGORY].map((format, idx) => {
              const subCat = format
              const isOverall = subCat === OVERALL_CATEGORY || !subCat

              const uniqueCategory = isOverall ? currentCat : subCat
              const uniqueKey = `jbp-planning-contractual-${uniqueCategory}`

              const tip = getPopupComment(idx)

              if (isCollapsed && !isOverall) {
                return <></>
              }

              return (
                <>
                  {isShowTotal && (
                    <tr
                      key={`jbp-target-actuals-${subCat}-${idx}`}
                      className={cn({ [styles.contractualTotalRow]: isUseContractuals })}
                    >
                      <td className={cn(styles.totalCell, styles.catTitle)}>
                        <Tooltip
                          inline
                          tipContent={() => (
                            <div className={styles.productLineTip}>
                              {tip ? (
                                <span>{tip}</span>
                              ) : (
                                <Translation text={totalTipKey} span actionsInside />
                              )}
                            </div>
                          )}
                          horizontalStartPosition={TPopupHorizontal.Right}
                          horizontalDirection={TPopupHorizontal.Right}
                        >
                          <div className={styles.productLine}>
                            <Translation text={totalKey} span actionsInside />
                          </div>
                        </Tooltip>
                        {/*{isUseContractuals && false && (*/}
                        {/*  <>*/}
                        {/*    <br />*/}
                        {/*    <Translation span text="jbp-planning-contractual-total" />*/}
                        {/*  </>*/}
                        {/*)}*/}
                      </td>
                      {renderQ(idx, -5, isEditAllowed, false, false)}
                      {renderQ(idx, 5, isEditAllowed, false, false)}
                      {renderQ(idx, 1, isEditAllowed, false, false)}
                      {renderQ(idx, 2, isEditAllowed, false, false)}
                      {renderQ(idx, 3, isEditAllowed, false, false)}
                      {renderQ(idx, 4, isEditAllowed, false, false)}
                      {isShowFutureYears && renderQ(idx, 6, isEditAllowed, false, false)}
                      {isShowFutureYears && renderQ(idx, 7, isEditAllowed, false, false)}
                    </tr>
                  )}

                  {!!partnerInfo?.IsGlobal && (
                    <tr
                      key={`jbp-target-rollup-${subCat}-${idx}`}
                      className={cn(styles.contractualRow, styles.rollupRow)}
                    >
                      <td className={cn(styles.catTitle)}>
                        <Translation text="jbp-planning-rollup" span actionsInside />
                      </td>
                      {renderQ(idx, -fyFq, isEditAllowed, false, true)}
                      {renderQ(idx, fyFq, isEditAllowed, false, true)}
                      {[1, 2, 3, 4].map((q) => renderQ(idx, q, isEditAllowed, false, true))}
                      {isShowFutureYears && renderQ(idx, fyFq + 1, isEditAllowed, false, true)}
                      {isShowFutureYears && renderQ(idx, fyFq + 2, isEditAllowed, false, true)}
                    </tr>
                  )}

                  {isUseContractuals && (
                    <>
                      {isShowContr && (
                        <tr
                          key={`${uniqueKey}-${idx}-contractual`}
                          className={styles.contractualRow}
                        >
                          <td className={styles.catTitle}>
                            <Translation span text="jbp-planning-contractual-contractual" />
                          </td>
                          {renderQContr(idx, -5, isEditAllowed, false, 'c')}
                          {renderQContr(idx, 5, isEditAllowed, false, 'c')}
                          {renderQContr(idx, 1, isEditAllowed, false, 'c')}
                          {renderQContr(idx, 2, isEditAllowed, false, 'c')}
                          {renderQContr(idx, 3, isEditAllowed, false, 'c')}
                          {renderQContr(idx, 4, isEditAllowed, false, 'c')}
                          {isShowFutureYears && renderQContr(idx, 6, isEditAllowed, false, 'c')}
                          {isShowFutureYears && renderQContr(idx, 7, isEditAllowed, false, 'c')}
                        </tr>
                      )}
                      {isShowTrans && (
                        <tr
                          key={`${uniqueKey}-${idx}-transactional`}
                          className={styles.contractualRow}
                        >
                          <td className={styles.catTitle}>
                            <Translation span text="jbp-planning-contractual-transactional" />
                          </td>
                          {renderQContr(idx, -5, isEditAllowed, false, 't')}
                          {renderQContr(idx, 5, isEditAllowed, false, 't')}
                          {renderQContr(idx, 1, isEditAllowed, false, 't')}
                          {renderQContr(idx, 2, isEditAllowed, false, 't')}
                          {renderQContr(idx, 3, isEditAllowed, false, 't')}
                          {renderQContr(idx, 4, isEditAllowed, false, 't')}
                          {isShowFutureYears && renderQContr(idx, 6, isEditAllowed, false, 't')}
                          {isShowFutureYears && renderQContr(idx, 7, isEditAllowed, false, 't')}
                        </tr>
                      )}
                    </>
                  )}

                  {isShowSoB && (
                    <tr
                      key={`jbp-planning-sob-${subCat}-${idx}`}
                      className={cn(styles.contractualRow, styles.sobRow)}
                    >
                      <td className={styles.catTitle}>
                        <div className={styles.sobTitle}>
                          <Translation text="jbp-planning-sob-row" />
                          <div className={styles.hintWrapper}>
                            <HintComponent
                              text="jbp-planning-sob-total-hint"
                              replaceParams={sobCalcCategoriesParams}
                            />
                          </div>
                        </div>
                      </td>
                      {renderSoB(idx, isOverall, -fyFq, isEditAllowed, false, false, false)}
                      {renderSoB(idx, isOverall, fyFq, isEditAllowed, false, false, false)}
                      {[1, 2, 3, 4].map((q) =>
                        renderSoB(idx, isOverall, q, isEditAllowed, false, false, false)
                      )}
                      {isShowFutureYears &&
                        renderSoB(idx, isOverall, fyFq + 1, isEditAllowed, false, false, false)}
                      {isShowFutureYears &&
                        renderSoB(idx, isOverall, fyFq + 2, isEditAllowed, false, false, false)}
                    </tr>
                  )}
                </>
              )
            })}
          </tbody>
        )}
      </table>
    </div>
  )
}
